import React, { useMemo, useState } from 'react'
import { Modal, Col, Row, Button } from 'react-bootstrap'
import type {
  IFilterData,
  IFilterDataArray,
} from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { ActiveFilterViewer, Filters } from '@rankmi/rankmi-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons'
import useUserColumnsFilters from 'hooks/users/useUserColumnsFilters'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { UserAttributes } from 'api/administration/participants/types'
import SelectUsers from './components/SelectUsers'

interface AddParticipantsModalProps {
  show: boolean
  onClose(): void
  onConfirm: (users: UserAttributes[]) => void | Promise<void>
  usersAdded?: UserAttributes[]
  filter?: IFilterDataArray[]
  saving?: boolean
}

const prefix = 'configuration.users.addParticipants'

const AddParticipantsModal: React.FC<AddParticipantsModalProps> = ({
  show,
  onClose,
  onConfirm,
  usersAdded = [],
  filter: initialFilterData,
  saving,
}) => {
  const [disabled, setDisabled] = useState(false)
  const [selectedUsersMap, setSelectedUsersMap] = useState<
    Map<number, UserAttributes>
  >(new Map())

  const selectedUsers = useMemo(
    () => Array.from(selectedUsersMap.values()),
    [selectedUsersMap]
  )

  const tRelative = useTranslationRankmi({
    prefix,
  })

  const [filterData, setFilterData] = useState<IFilterData>()

  const filterDataMerge = useMemo(
    () => ({
      filterType: 'simple',
      ...filterData,
      data: [...(filterData?.data || []), ...(initialFilterData || [])],
    }),
    [filterData, initialFilterData]
  )

  const [showFiltersModal, setShowFiltersModal] = useState(false)

  const { filtersColumns } = useUserColumnsFilters()

  const handleOnClose = () => {
    onClose()
    setShowFiltersModal(false)
  }

  const handleOnConfirm = async () => {
    await onConfirm(selectedUsers)
    setShowFiltersModal(false)

    setSelectedUsersMap(new Map())
  }

  const valid = selectedUsersMap.size > 0

  return (
    <Modal
      size="xl"
      show={show}
      onHide={handleOnClose}
      backdrop={!showFiltersModal}
      style={{
        display: showFiltersModal ? 'none' : 'block',
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{tRelative(`title`)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="position-relative">
          <Col xs={12}>
            <Filters
              columns={filtersColumns}
              onChange={(value) => {
                setFilterData(value)
              }}
              open={showFiltersModal}
              onVisibilityChange={(state: boolean) => {
                setShowFiltersModal(state)
              }}
              selectedFilters={filterData}
            />
          </Col>
          {filterData && (
            <Col xs={12} className="mt-4">
              <ActiveFilterViewer
                activeFilters={filterData}
                onChange={(data) => setFilterData(data)}
                openFilters={(state: boolean) => setShowFiltersModal(state)}
                columns={filtersColumns}
              />
            </Col>
          )}
          <Col xs={12}>
            <SelectUsers
              selectedMap={selectedUsersMap}
              usersAdded={usersAdded}
              onAdd={(users) => {
                const newSelectedUsers = new Map(selectedUsersMap)
                users.forEach((user) => {
                  newSelectedUsers.set(user.id, user)
                })

                setSelectedUsersMap(newSelectedUsers)
              }}
              onRemove={(users) => {
                const newSelectedUsers = new Map(selectedUsersMap)
                users.forEach((user) => {
                  newSelectedUsers.delete(user.id)
                })
                setSelectedUsersMap(newSelectedUsers)
              }}
              filterData={filterDataMerge}
              onStartLoading={() => {
                setDisabled(true)
              }}
              onStopLoading={() => {
                setDisabled(false)
              }}
            />
          </Col>
          <Col xs={12}>
            <div className="d-flex justify-content-end mt-2">
              <Button
                variant="secondary"
                className="mr-2"
                onClick={handleOnClose}
              >
                {tRelative(`buttons.cancel`)}
              </Button>
              <Button
                disabled={disabled || saving || !valid}
                onClick={() => {
                  handleOnConfirm()
                }}
              >
                {saving && (
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                )}
                {tRelative(`buttons.add`)}
              </Button>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default AddParticipantsModal
